// Layer 4 (UI) — the Risk register tab (polish.P20, comp A `regTable`,
// `matrixCard`, `detailCard`; B's US$ bars in the Budget impact column). The
// register ranked by US$ with the selected row washed and ruled; beneath it,
// safety against production on two axes and the selected risk's arithmetic.
// Renders `riskRegister()`, `riskMatrix()`, `impactEquation()` and
// `riskDetailFacts()` only.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { cn } from '@/shared/cn';
import { DowntimeCard, SYN_CHIP } from '../downtime/DowntimeCard';
import { EDGE_TABLE, LEAD_ROW } from '../maintenance/MaintenanceTable';
import {
  MATRIX_VIEW,
  formatBudgetCurrency,
  formatImpactQuantity,
  impactEquation,
  riskDetailFacts,
  riskMatrix,
  type RiskMatrixPoint,
  type RiskRegisterRow,
} from './Scene.transform';
import { BandChip, IdCode, OwnerChip, REF_CHIP, Segments } from './risk-ui';

/** The Budget impact cell: the US$ as the cell's first text node (the bearing
 *  UI probe reads it there), the quantity beside it, the rank bar beneath —
 *  crimson for rank 1, ink-secondary for the rest, an empty track for US$0. */
function BudgetImpactCell({ row }: { row: RiskRegisterRow }) {
  const zero = row.impact.dollars === 0;
  return (
    <TD
      className={cn(
        'min-w-[9.5rem] whitespace-nowrap !pr-4 tabular-nums',
        zero ? 'font-medium !text-muted' : 'font-semibold !text-ink-primary',
      )}
      data-budget-impact={row.id}
    >
      {formatBudgetCurrency(row.impact)}
      <span className="ml-1.5 text-[11.5px] font-normal text-muted">{formatImpactQuantity(row.impact)}</span>
      <div className="relative mt-1 h-[5px] overflow-hidden rounded-[3px] bg-[var(--hairline)]" aria-hidden data-impact-bar={row.rank === 1 ? 'top' : 'ink'}>
        <i
          className="absolute inset-y-0 left-0 block rounded-[3px]"
          style={{
            width: `${zero ? 0 : Math.max(row.barRatio * 100, 1.5)}%`,
            backgroundColor: row.rank === 1 ? 'var(--color-accent-500)' : 'var(--ink-secondary)',
          }}
        />
      </div>
    </TD>
  );
}

function RegisterTable({
  rows,
  selectedId,
  onSelect,
}: {
  rows: readonly RiskRegisterRow[];
  selectedId: string | undefined;
  onSelect: (id: string) => void;
}) {
  return (
    <DowntimeCard
      title="Risk register"
      meta="ranked by US$ impact · RPN breaks ties"
      actions={SYN_CHIP}
      className="px-0 pb-1 [&>header]:px-4"
    >
      <div className={cn(EDGE_TABLE, '[&_td]:!align-middle')}>
        <Table wrap={false} className="tabular-nums">
          <THead>
            <TR>
              <TH>ID</TH>
              <TH>Priority</TH>
              <TH>Area</TH>
              <TH>Risk</TH>
              <TH className="text-right">Score</TH>
              <TH>Owner</TH>
              <TH>Review</TH>
              <TH className="text-right">Safety</TH>
              <TH className="text-right">Production</TH>
              <TH className="text-right">RPN</TH>
              <TH>Budget impact</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              return (
                <TR
                  key={row.id}
                  onClick={() => onSelect(row.id)}
                  aria-selected={selected}
                  data-risk-row={row.id}
                  className={selected ? cn('cursor-pointer', LEAD_ROW) : 'cursor-pointer'}
                >
                  <TD className="whitespace-nowrap font-mono text-[11.5px] !text-ink-primary">{row.id}</TD>
                  <TD><BandChip band={row.band} label={row.bandLabel} /></TD>
                  <TD className="whitespace-nowrap text-[11.5px] !text-muted">{row.circuit}</TD>
                  <TD className="min-w-[8rem] text-[13px] font-semibold leading-tight !text-ink-primary">{row.label}</TD>
                  <TD className="text-right text-[13px] font-semibold !text-ink-primary">{row.score}</TD>
                  <TD><OwnerChip>{row.ownerTeam}</OwnerChip></TD>
                  <TD className="whitespace-nowrap">{row.reviewCadence}</TD>
                  <TD className="text-right">{row.safetySeverity}</TD>
                  <TD className="text-right">{row.productionSeverity}</TD>
                  <TD className="text-right">{row.rpn}</TD>
                  <BudgetImpactCell row={row} />
                </TR>
              );
            })}
          </TBody>
        </Table>
      </div>
    </DowntimeCard>
  );
}

const { height: H, padLeft: PL, padTop: PT, padRight: PR, padBottom: PB } = MATRIX_VIEW;
const PH = H - PT - PB;

/** The chart's drawn width: the card's content width, measured, so one
 *  viewBox unit is one pixel and the ticks and labels hold their size. */
function useMeasuredWidth(fallback: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const read = () => setWidth(Math.max(320, Math.round(node.getBoundingClientRect().width)));
    read();
    const observer = new ResizeObserver(read);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

function labelPlacement(point: RiskMatrixPoint, x: number, y: number, selected: boolean) {
  const off = selected ? 13 : 10;
  switch (point.labelSide) {
    case 'left':
      return { x: x - off, y: y + 3.5, anchor: 'end' as const };
    case 'top':
      return { x, y: y - off, anchor: 'middle' as const };
    case 'bottom':
      return { x, y: y + off + 7, anchor: 'middle' as const };
    case 'right':
    default:
      return { x: x + off, y: y + 3.5, anchor: 'start' as const };
  }
}

function RiskMatrixChart({ selectedId }: { selectedId?: string }) {
  const [ref, W] = useMeasuredWidth(MATRIX_VIEW.width);
  const PW = W - PL - PR;
  const px = (pct: number) => PL + (pct / 100) * PW;
  const py = (pct: number) => PT + (pct / 100) * PH;
  const points = riskMatrix(PW);
  return (
    <div ref={ref} className="min-w-0">
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      role="img"
      aria-label="Risk matrix: production severity across, safety severity up"
      className="block overflow-visible"
      data-risk-matrix
    >
      <rect x={PL} y={PT} width={PW} height={PH} rx={8} fill="var(--surface-1)" stroke="var(--hairline-strong)" />
      <line x1={px(50)} y1={PT} x2={px(50)} y2={PT + PH} stroke="var(--hairline-strong)" strokeDasharray="3 3" />
      <line x1={PL} y1={py(50)} x2={PL + PW} y2={py(50)} stroke="var(--hairline-strong)" strokeDasharray="3 3" />
      {[0, 5, 10].map((tick) => (
        <g key={tick} fontSize={10} fill="var(--ink-tertiary)" className="tabular-nums">
          <text x={px(tick * 10)} y={PT + PH + 14} textAnchor="middle">{tick}</text>
          <text x={PL - 8} y={py(100 - tick * 10) + 3} textAnchor="end">{tick}</text>
        </g>
      ))}
      <text x={PL + PW} y={H - 3} fontSize={10.5} fill="var(--ink-tertiary)" textAnchor="end">Production severity →</text>
      <text x={PL + 8} y={PT + 14} fontSize={10.5} fill="var(--ink-tertiary)">Safety severity ↑</text>
      <g fontSize={10.5} className="font-mono tabular-nums">
        {points.map((point) => {
          const x = px(point.xPct);
          const y = py(point.yPct);
          const selected = point.id === selectedId;
          const label = labelPlacement(point, x, y, selected);
          return (
            <g key={point.id} data-matrix-point={point.id} data-at-constraint={point.atConstraint ? 'true' : 'false'}>
              <title>{`${point.id} · ${point.label}`}</title>
              {selected ? <circle cx={x} cy={y} r={8.5} fill="none" stroke="var(--color-accent-500)" strokeWidth={1.75} /> : null}
              <circle
                cx={x}
                cy={y}
                r={4.5}
                fill={point.atConstraint ? 'var(--ink-primary)' : 'var(--surface-2)'}
                stroke={point.atConstraint ? 'var(--ink-primary)' : 'var(--ink-secondary)'}
                strokeWidth={1.5}
              />
              <text x={label.x} y={label.y} textAnchor={label.anchor} fill="var(--ink-secondary)">{point.id}</text>
            </g>
          );
        })}
      </g>
    </svg>
    </div>
  );
}

function MatrixLegend() {
  return (
    <span className="flex items-center gap-3 text-[11px] text-muted">
      <span className="inline-flex items-center gap-1.5">
        <i aria-hidden className="inline-block size-2.5 rounded-full bg-ink-primary" />
        at the constraint
      </span>
      <span className="inline-flex items-center gap-1.5">
        <i aria-hidden className="inline-block size-2.5 rounded-full border-[1.5px] border-[var(--ink-secondary)] bg-[var(--surface-2)]" />
        elsewhere
      </span>
    </span>
  );
}

function FactRow({ label, children, long = false }: { label: string; children: ReactNode; long?: boolean }) {
  return (
    <div
      className={cn(
        'grid gap-3 border-b border-[var(--color-divider)] py-1.5 text-[12.5px] last:border-b-0',
        long ? 'grid-cols-1 gap-1' : 'grid-cols-[auto_minmax(0,1fr)] items-baseline',
      )}
    >
      <span className="whitespace-nowrap text-muted">{label}</span>
      <span className={long ? 'leading-snug text-ink-secondary' : 'text-right font-medium text-ink-primary'}>{children}</span>
    </div>
  );
}

function RiskDetail({ row }: { row: RiskRegisterRow }) {
  const equation = impactEquation(row);
  const facts = riskDetailFacts(row);
  return (
    <DowntimeCard
      aria-label={`${row.id} ${row.label}`}
      className="h-full"
      title={undefined}
    >
      <header className="mb-2.5 flex flex-wrap items-center gap-2" data-risk-detail={row.id}>
        <h3 className="flex items-baseline gap-2 text-sm font-semibold text-ink-primary">
          <IdCode>{row.id}</IdCode>
          {row.label}
        </h3>
        <span className="flex-1" />
        <BandChip band={row.band} label={row.bandLabel} />
      </header>
      <div
        className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-y-1 rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] px-3 py-2.5 text-[12.5px] tabular-nums"
        data-risk-equation
      >
        {equation.rows.map((item) => (
          <div key={item.label} className="contents" data-equation-row={item.label}>
            <span className="text-muted">{item.label}</span>
            <span className="pl-3 text-right font-medium text-ink-primary">{item.value}</span>
          </div>
        ))}
        <span aria-hidden className="col-span-2 my-0.5 border-t border-[var(--hairline-strong)]" />
        <span className="text-muted">Budget impact</span>
        <span className="pl-3 text-right text-[17px] font-semibold text-ink-primary" data-equation-total>
          {equation.total}
        </span>
        {equation.comparison.length > 0 ? (
          <span className="col-span-2 text-[11.5px] text-muted" data-equation-comparison>
            <Segments segments={equation.comparison} />
          </span>
        ) : null}
      </div>
      <div className="mt-2">
        <FactRow label="Likelihood">{facts.likelihood}</FactRow>
        <FactRow label="Owner"><OwnerChip>{facts.owner}</OwnerChip></FactRow>
        <FactRow label="Review date">{facts.review}</FactRow>
        {facts.workOrder ? <FactRow label="Linked work order"><span className={REF_CHIP}>{facts.workOrder}</span></FactRow> : null}
        {facts.failureMode ? <FactRow label="Failure mode">{facts.failureMode}</FactRow> : null}
        <FactRow label="Control measure" long>{facts.controlMeasure}</FactRow>
      </div>
    </DowntimeCard>
  );
}

export function RiskRegisterTab({
  rows,
  selectedId,
  onSelect,
}: {
  rows: readonly RiskRegisterRow[];
  selectedId: string | undefined;
  onSelect: (id: string) => void;
}) {
  const selected = rows.find((row) => row.id === selectedId) ?? rows[0];
  return (
    <div className="space-y-3">
      <RegisterTable rows={rows} selectedId={selected?.id} onSelect={onSelect} />
      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-2">
        <DowntimeCard title="Two axes · safety against production" actions={<MatrixLegend />}>
          <RiskMatrixChart selectedId={selected?.id} />
        </DowntimeCard>
        {selected ? <RiskDetail row={selected} /> : null}
      </div>
    </div>
  );
}
